import { logger } from '../utils/logger';

const OFFSCREEN_DOCUMENT_PATH = 'src/offscreen/offscreen.html';

// How long to wait for the offscreen document to respond to a single inference request.
// The first call after a SW restart may take 30-60 s while the WASM model loads from
// cache; 180 s is well above that ceiling while still bounding the SW's wait to prevent
// a permanent queue stall when the offscreen document crashes or hangs silently.
const EMBEDDING_MESSAGE_TIMEOUT_MS = 180_000;

class EmbeddingProxy {
  private static instance: EmbeddingProxy;
  private creating: Promise<void> | null = null;

  private constructor() {}

  public static getInstance(): EmbeddingProxy {
    if (!EmbeddingProxy.instance) {
      EmbeddingProxy.instance = new EmbeddingProxy();
    }
    return EmbeddingProxy.instance;
  }

  private async setupOffscreenDocument(path: string) {
    // Check if offscreen document already exists
    const existingContexts = await chrome.runtime.getContexts({
      contextTypes: [chrome.runtime.ContextType.OFFSCREEN_DOCUMENT],
      documentUrls: [chrome.runtime.getURL(path)],
    });

    if (existingContexts.length > 0) {
      logger.debug('[EmbeddingProxy] Offscreen document already exists, reusing.');
      return;
    }

    // Create offscreen document
    if (this.creating) {
      logger.debug('[EmbeddingProxy] Offscreen document creation already in progress, waiting...');
      await this.creating;
    } else {
      logger.info('[EmbeddingProxy] Creating offscreen document for WASM embedding...');
      this.creating = chrome.offscreen.createDocument({
        url: path,
        reasons: [chrome.offscreen.Reason.DOM_PARSER],
        justification: 'To run the @huggingface/transformers library for embedding generation',
      });
      try {
        await this.creating;
        logger.info('[EmbeddingProxy] Offscreen document created successfully.');
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        logger.error(`[EmbeddingProxy] Failed to create offscreen document: ${msg}`, e);
        throw e;
      } finally {
        // Always clear so a rejected promise does not permanently block future attempts.
        this.creating = null;
      }
    }
  }

  public async init() {
      await this.setupOffscreenDocument(OFFSCREEN_DOCUMENT_PATH);
  }

  public async generateEmbedding(text: string, signal?: AbortSignal): Promise<number[]> {
    await this.setupOffscreenDocument(OFFSCREEN_DOCUMENT_PATH);

    // Send message to offscreen document with retries for "Receiving end does not exist"
    let retries = 5;
    while (retries > 0) {
        if (signal?.aborted) throw new DOMException('Embedding aborted', 'AbortError');
        try {
            // Race the message against the abort signal and a hard timeout.
            // If the offscreen document crashes or hangs without responding,
            // the timeout prevents the queue processor from waiting indefinitely.
            const messagePromise = chrome.runtime.sendMessage({
              type: 'generateEmbedding',
              text,
            });
            const timeoutPromise = new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Embedding message timeout')), EMBEDDING_MESSAGE_TIMEOUT_MS)
            );
            const response = signal
              ? await Promise.race([
                  messagePromise,
                  new Promise<never>((_, reject) =>
                    signal.addEventListener('abort', () => reject(new DOMException('Embedding aborted', 'AbortError')), { once: true })
                  ),
                  timeoutPromise,
                ])
              : await Promise.race([messagePromise, timeoutPromise]);

            if (response.success) {
                return response.embedding;
            } else {
                throw new Error(response.error || 'Unknown error in offscreen embedding generation');
            }
        } catch (e: any) {
             if (e.name === 'AbortError') throw e;
             if (e.message?.includes('Receiving end does not exist')) {
                 logger.warn(`[Embeddings] Offscreen document not ready yet. Retrying in 100ms... (${retries - 1} left)`);
                 // Recreate the document in case it crashed or was closed since init().
                 await this.setupOffscreenDocument(OFFSCREEN_DOCUMENT_PATH);
                 await new Promise(resolve => setTimeout(resolve, 100));
                 retries--;
             } else {
                 throw e;
             }
        }
    }

    throw new Error('Failed to communicate with offscreen document after multiple retries.');
  }

  /**
   * Close the offscreen document and free its WASM heap.
   * The WASM memory grows monotonically during inference and never shrinks within
   * a document lifetime. Call this after each embedding batch to prevent OOM when
   * processing large queues — the next init() call will recreate the document fresh.
   */
  public async closeDocument(): Promise<void> {
    try {
      const existingContexts = await chrome.runtime.getContexts({
        contextTypes: [chrome.runtime.ContextType.OFFSCREEN_DOCUMENT],
        documentUrls: [chrome.runtime.getURL(OFFSCREEN_DOCUMENT_PATH)],
      });
      if (existingContexts.length > 0) {
        await chrome.offscreen.closeDocument();
        logger.info('[EmbeddingProxy] Closed offscreen document (WASM heap freed).');
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      logger.warn(`[EmbeddingProxy] Failed to close offscreen document: ${msg}`);
      throw e;
    }
  }

  /**
   * Generate embeddings for multiple texts in a single batched ONNX inference call.
   * Prefer this over N individual generateEmbedding() calls — the WASM runtime
   * vectorises matrix operations across the batch dimension, giving a meaningful
   * speedup over sequential single-text requests.
   */
  public async generateEmbeddings(texts: string[], signal?: AbortSignal): Promise<number[][]> {
    if (texts.length === 0) return [];
    await this.setupOffscreenDocument(OFFSCREEN_DOCUMENT_PATH);

    let retries = 5;
    while (retries > 0) {
      if (signal?.aborted) throw new DOMException('Embedding aborted', 'AbortError');
      try {
        const messagePromise = chrome.runtime.sendMessage({
          type: 'generateEmbeddings',
          texts,
        });
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Embedding message timeout')), EMBEDDING_MESSAGE_TIMEOUT_MS)
        );
        const response = signal
          ? await Promise.race([
              messagePromise,
              new Promise<never>((_, reject) =>
                signal.addEventListener('abort', () => reject(new DOMException('Embedding aborted', 'AbortError')), { once: true })
              ),
              timeoutPromise,
            ])
          : await Promise.race([messagePromise, timeoutPromise]);

        if (response.success) {
          return response.embeddings;
        } else {
          const errDetail = response.error || 'Unknown error in offscreen batch embedding generation';
          logger.error(`[EmbeddingProxy] Offscreen returned failure response: ${errDetail}`);
          throw new Error(errDetail);
        }
      } catch (e: any) {
        if (e.name === 'AbortError') throw e;
        if (e.message?.includes('Receiving end does not exist')) {
          logger.warn(`[EmbeddingProxy] Offscreen document not ready (Receiving end does not exist). Recreating and retrying... (${retries - 1} retries left)`);
          // Recreate the document in case it crashed or was closed since init().
          await this.setupOffscreenDocument(OFFSCREEN_DOCUMENT_PATH);
          await new Promise(resolve => setTimeout(resolve, 100));
          retries--;
        } else {
          const msg = e instanceof Error ? e.message : String(e);
          const stack = e instanceof Error && e.stack ? `\n${e.stack}` : '';
          logger.error(`[EmbeddingProxy] generateEmbeddings error (${retries} retries remaining): ${msg}${stack}`);
          throw e;
        }
      }
    }

    throw new Error('Failed to communicate with offscreen document after multiple retries.');
  }
}

export const embeddingProxy = EmbeddingProxy.getInstance();
